import React, { useEffect, useState } from "react";
import {
  FlatList,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { Feather, MaterialCommunityIcons } from "@expo/vector-icons";

import RestrictionCard from "./Components/RestrictionCard";

import ScreenLayout from "@/Components/Layouts/Native/ScreenLayout";
import { type AppUserSession } from "@/schemas/auth/session";
import {
  type UserRestriction,
  UserRestrictionType,
} from "@/schemas/user/restriction";
import RestrictionService from "@/services/user/restriction";

type ScreenProps = {
  session: AppUserSession;
};

function ManageBLockedUsersScreen({ session }: ScreenProps): React.JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [restrictions, setRestrictions] = useState<UserRestriction[]>([]);

  const fetchRestrictions = async (): Promise<void> => {
    setIsLoading(true);
    const restrictionService = new RestrictionService(session);

    try {
      const response = await restrictionService.getUserRestrictions(
        UserRestrictionType.BLOCKED,
      );
      setRestrictions(response.items);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchRestrictions();
  }, []);

  const renderRestriction = ({
    item,
  }: {
    item: UserRestriction;
  }): React.JSX.Element => {
    return <RestrictionCard item={item} />;
  };

  return (
    <ScreenLayout isLoading={isLoading}>
      <ScrollView style={styles.content}>
        {restrictions.length === 0 ? (
          <View style={styles.container}>
            <MaterialCommunityIcons
              name="block-helper"
              size={120}
              color="#888"
              style={styles.icon}
            />
            <Text style={styles.emptyTitle}>
              You have not blocked any users yet.
            </Text>
          </View>
        ) : (
          <View>
            <View style={styles.alert}>
              <View style={styles.alertHeader}>
                <Feather name="alert-circle" size={20} color="#ef4444" />
                <Text style={styles.alertTitle}>Warning</Text>
              </View>
              <Text style={styles.alertDescription}>
                Blocked users are individuals you have chosen to restrict from
                interacting with you. They won&apos;t be able to send you
                messages.
              </Text>
            </View>
            <FlatList
              data={restrictions}
              renderItem={(restriction) => renderRestriction(restriction)}
              keyExtractor={(item) => item.restriction.id}
              refreshControl={
                <RefreshControl
                  refreshing={isLoading}
                  onRefresh={fetchRestrictions}
                />
              }
            />
          </View>
        )}
      </ScrollView>
    </ScreenLayout>
  );
}

const styles = StyleSheet.create({
  content: {
    flex: 1,
    padding: 16,
  },
  container: {
    alignItems: "center",
    flex: 1,
    justifyContent: "center",
    padding: 20,
  },
  emptyTitle: {
    color: "#888",
    fontSize: 24,
    fontWeight: "bold",
    marginBottom: 10,
  },
  icon: {
    marginBottom: 20,
  },
  alert: {
    backgroundColor: "#fef2f2",
    borderColor: "#fef2f2",
    borderRadius: 8,
  },
  alertTitle: {
    fontSize: 20,
    fontWeight: "600",
    color: "#ef4444",
    marginLeft: 8,
  },
  alertHeader: {
    flexDirection: "row",
    alignItems: "center",
  },
  alertDescription: {
    marginTop: 8,
    fontSize: 14,
    color: "#ef4444",
  },
});

export default ManageBLockedUsersScreen;
